// SD60: which source the player gets, and keeping it steady.
//
// A `processing` grackle plays progressively from its already-complete master while the
// publish job works (playback.rs). The watch page keeps polling until the job finishes, and
// every poll can bring a re-signed master URL — or, if muxing is automatic, an HLS manifest
// once the job commits. Handing either to the player would reload the video and throw the
// viewer back to the start, so a source first obtained while `processing` is kept for as long
// as the page shows this grackle. A source obtained on a `ready` grackle is only kept across a
// re-sign of the same kind; a change of kind (an on-demand remux landing) still switches,
// as it always did.
import { useRef } from "react";
import type { WatchOut } from "../api/types";

export type PlayableSource = { src: string; kind: "hls" | "progressive" };

type Pinned = PlayableSource & { grackleId: string; duringProcessing: boolean };

/** The source the server offers right now, or null when there is nothing to play. */
export function offeredSource(watch: WatchOut): PlayableSource | null {
  const { status } = watch.grackle;
  if (status !== "ready" && status !== "processing") return null;
  if (watch.manifest_url) return { src: watch.manifest_url, kind: "hls" };
  if (watch.progressive_url) return { src: watch.progressive_url, kind: "progressive" };
  return null;
}

/** Pure step of the pinning rule, for tests: what to play given what was playing. */
export function nextPinned(prev: Pinned | null, watch: WatchOut): Pinned | null {
  const offered = offeredSource(watch);
  if (!offered) return null;
  const grackleId = watch.grackle.id;
  if (prev && prev.grackleId === grackleId && (prev.duringProcessing || prev.kind === offered.kind)) {
    return prev;
  }
  return { ...offered, grackleId, duringProcessing: watch.grackle.status === "processing" };
}

/** The source to play, and `refresh` for the player's `onSourceExpired`: it drops the pin so
 * the freshly signed URL the next `reload` brings is taken, then reloads (FR-ST18 — a pinned
 * URL outlives its signature on a page left open long enough). */
export function usePlayableSource(
  watch: WatchOut,
  reload?: () => unknown,
): { source: PlayableSource | null; refresh?: () => void } {
  const pinned = useRef<Pinned | null>(null);
  const refreshing = useRef(false);
  const offered = offeredSource(watch);
  if (refreshing.current && offered && pinned.current && offered.src !== pinned.current.src) {
    refreshing.current = false;
    pinned.current = null;
  }
  pinned.current = nextPinned(pinned.current, watch);
  const source = pinned.current ? { src: pinned.current.src, kind: pinned.current.kind } : null;
  const refresh = reload
    ? () => {
        refreshing.current = true;
        void reload();
      }
    : undefined;
  return { source, refresh };
}
